import { Rank, type EnemyHookContext } from '../../sim/defs';
import { defineEnemy, Motion } from '../schema';

/** E-I3 — LAME. L'intercepteur qui COUPE LA ROUTE.
 *
 *  Elle tombe droit jusqu'à la rangée basse — celle où le joueur passe son
 *  temps — puis se rabat à l'horizontale et FILE d'un bord à l'autre, vite, sans
 *  jamais tirer. Un corps lancé pile en travers du couloir d'esquive.
 *
 *  ── Ce qu'elle enseigne ──
 *  Que l'esquive latérale a un coût. Dans un shmup, tout le danger vient d'en
 *  haut et le réflexe est de glisser à l'horizontale pour l'éviter. La Lame vient
 *  précisément COUPER cette horizontale : rester dans sa rangée en se déplaçant
 *  de côté, c'est courir à sa rencontre. La parade est VERTICALE — se hisser
 *  au-dessus d'elle le temps qu'elle passe, ou se glisser derrière — un axe de
 *  mouvement que le joueur n'emploie presque jamais. Deux Lames lancées à
 *  contresens depuis les deux bords se coupent au centre : le couloir se referme
 *  des deux côtés en même temps.
 *
 *  ── Le hook plutôt que Motion.Strafe (PLAN.md §3.3) ──
 *  Strafe conviendrait presque, mais il recalcule sa direction depuis le SIGNE de
 *  l'abscisse à chaque tick : une fois le centre franchi, il rebrousse et
 *  s'immobilise au milieu. La Lame doit COMMETTRE une direction et sortir par le
 *  bord opposé. On fixe donc le sens dans la définition — d'où deux archétypes,
 *  `lame-a` (file vers la droite) et `lame-b` (vers la gauche) — et le hook, pur
 *  et sans allocation, ne fait qu'intégrer une vitesse horizontale constante.
 *
 *  ── Pourquoi elle tombe DROIT avant de filer ──
 *  Le hook ne tranche qu'une fois arrivée à la rangée (`ctx.y <= ROW`) : au-dessus,
 *  elle chute verticalement, portée par le moteur (Hover), avec son accélération
 *  d'entrée et sa protection d'apparition. C'est ce qui rend la coupe LISIBLE —
 *  une chute nette, puis un trait horizontal franc — au lieu d'une oblique molle
 *  qu'on ne saurait pas anticiper.
 *
 *  ── Ce qui garde l'esquive vivante ──
 *  La trajectoire est droite, télégraphiée, et n'accélère pas : à 460 u/s la Lame
 *  traverse en moins de 2 s, largement le temps de la lire et de l'enjamber. Elle
 *  ne poursuit rien — elle occupe une ligne, elle ne vous cherche pas. */

/** Ordonnée de la rangée basse où la Lame se met à filer, en unités. Juste
 *  au-dessus de la position de repos du joueur (−380) : elle balaie sa ligne. */
const ROW = -360;
/** Vitesse du balayage horizontal, en u/s. Vive mais sous les 480 du joueur, et
 *  surtout rectiligne — on l'esquive en changeant d'axe, pas en la distançant. */
const SLASH = 460;

function trancher(sens: number) {
  return (slot: number, _age: number, dt: number, ctx: EnemyHookContext): void => {
    // Encore en descente : on laisse le moteur (Hover) la faire tomber droit.
    // Marge de 4 u : Hover ne fige pas exactement à ROW, il dépasse d'un tick.
    if (ctx.y[slot]! > ROW + 4) return;
    ctx.x[slot] = ctx.x[slot]! + sens * SLASH * dt;
  };
}

const base = {
  rank: Rank.Standard,
  hp: 70,
  visualRadius: 22,
  score: 160,
  telegraph: 0.8,
  drops: 1,
  // Hover pour DESCENDRE jusqu'à la rangée basse et y figer l'ordonnée (amplitude
  // nulle : pas d'oscillation moteur). Le balayage horizontal est ensuite tout au
  // hook.
  motion: { kind: Motion.Hover, speed: 360, amplitude: 0, frequency: 0, holdY: ROW },
} as const;

const lameA = defineEnemy({
  ...base,
  id: 'lame-a',
  onUpdate: trancher(1),
});
const lameB = defineEnemy({
  ...base,
  id: 'lame-b',
  onUpdate: trancher(-1),
});

export default [lameA, lameB];
